import { useEffect, useRef, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import moment from "moment";
import QRCode from "qrcode";
import {
  checkInCamp,
  checkOutCamp,
  decampCamp,
  getMealHistory,
  getUser,
  getStaff,
  getAccommodationRooms,
  updateAccommodationBunk,
  recordMeal,
  recordAttendance,
  markTabletCollected,
  getUserAccommodationLogs,
  getInfantsByMsp,
  checkInInfant,
  checkOutInfant,
  getTodayStaffAttendance,
  getStaffSchedules,
  checkInStaff,
  checkOutStaff,
  temporaryCheckOutStaff,
  temporaryCheckInStaff,
  generateUserId,
} from '../../apis';
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import IDCard from "../../components/IDCard";
import fileUpload from "../../utils/fileUpload";
import AdminLayout from "./Layout";
import { useSelector } from "react-redux";
import StatusModal from "../../components/StatusModal";
import AttendanceModal from "../../components/AttendanceModal";
import TabChecking from "../../components/TabChecking";
import { getGMT1StartOfDay, isSameGMT1Date } from "../../utils/dateUtils";

const getInitialsFromName = (name = "") => {
  const safeName = typeof name === "string" ? name.trim() : "";
  if (!safeName) return "MS";
  const parts = safeName.split(/\s+/);
  const first = parts[0]?.charAt(0) || "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.charAt(0) : "";
  const initials = `${first}${last}`.toUpperCase();
  return initials || safeName.substring(0, 2).toUpperCase();
};

// Staff Attendance Section Component
const StaffAttendanceSection = ({ staffId, auth }) => {
  const [attendance, setAttendance] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [showTempCheckoutModal, setShowTempCheckoutModal] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [tempCheckoutData, setTempCheckoutData] = useState({
    errandName: "",
    reason: "",
  });

  useEffect(() => {
    if (!auth?.token || !staffId) return;
    fetchAttendance();
  }, [auth?.token, staffId]);


  const fetchAttendance = async () => {
    setLoading(true);
    try {
      const today = getGMT1StartOfDay();
      const todayStr = moment(today).format('YYYY-MM-DD');
      
      // Fetch today's schedules
      const schedulesResponse = await getStaffSchedules({
        token: auth.token,
        page: 1,
        limit: 100,
        staffId: staffId,
        startDate: todayStr,
        endDate: todayStr,
      });

      // Fetch today's attendance
      const attendanceResponse = await getTodayStaffAttendance({
        token: auth.token,
        staffId: staffId,
      });

      if (schedulesResponse?.status === 200) {
        setSchedules(schedulesResponse?.data?.data || []);
      }

      if (attendanceResponse?.status === 200) {
        setAttendance(attendanceResponse?.data?.data || []);
      }
    } catch (error) {
      console.error("Error fetching staff attendance:", error);
    } finally {
      setLoading(false);
    }
  };

  const getAttendanceForSchedule = (scheduleId) => {
    return attendance.find((a) => a.schedule?._id === scheduleId || a.schedule === scheduleId);
  };

  const handleCheckIn = async (scheduleId) => {
    if (!auth?.token) return;
    setLoading(true);
    setActionMessage("");
    try {
      const response = await checkInStaff({ scheduleId }, auth.token);
      if (response?.status === 201) {
        setActionMessage("Checked in successfully");
        fetchAttendance();
      } else {
        setActionMessage(response?.data?.message || "Unable to check in");
      }
    } catch (error) {
      setActionMessage("An error occurred while checking in");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async (attendanceId) => {
    if (!auth?.token) return;
    setLoading(true);
    setActionMessage("");
    try {
      const response = await checkOutStaff({ attendanceId }, auth.token);
      if (response?.status === 200) {
        setActionMessage("Checked out successfully");
        fetchAttendance();
      } else {
        setActionMessage(response?.data?.message || "Unable to check out");
      }
    } catch (error) {
      setActionMessage("An error occurred while checking out");
    } finally {
      setLoading(false);
    }
  };

  const handleTempCheckout = async () => {
    if (!tempCheckoutData.errandName.trim() || !tempCheckoutData.reason.trim()) {
      setActionMessage("Please fill in errand name and reason");
      return;
    }

    if (!auth?.token || !selectedAttendance) return;
    setLoading(true);
    setActionMessage("");
    try {
      const response = await temporaryCheckOutStaff(
        {
          attendanceId: selectedAttendance._id,
          errandName: tempCheckoutData.errandName,
          reason: tempCheckoutData.reason,
        },
        auth.token
      );
      if (response?.status === 200) {
        setActionMessage("Temporary checkout recorded successfully");
        setShowTempCheckoutModal(false);
        setTempCheckoutData({ errandName: "", reason: "" });
        setSelectedAttendance(null);
        fetchAttendance();
      } else {
        setActionMessage(response?.data?.message || "Unable to record temporary checkout");
      }
    } catch (error) {
      setActionMessage("An error occurred while recording temporary checkout");
    } finally {
      setLoading(false);
    }
  };

  const handleTempCheckin = async (attendanceId, tempCheckoutId) => {
    if (!auth?.token) return;
    setLoading(true);
    setActionMessage("");
    try {
      const response = await temporaryCheckInStaff(
        {
          attendanceId,
          tempCheckoutId,
        },
        auth.token
      );
      if (response?.status === 200) {
        setActionMessage("Checked back in from temporary checkout");
        fetchAttendance();
      } else {
        setActionMessage(response?.data?.message || "Unable to check back in");
      }
    } catch (error) {
      setActionMessage("An error occurred while checking back in");
    } finally {
      setLoading(false);
    }
  };

  const getActiveTempCheckout = (attendanceRecord) => {
    return attendanceRecord?.temporaryCheckouts?.find(
      (tc) => tc.checkedOutAt && !tc.checkedInAt
    );
  };

  const formatTime = (dateString) => {
    if (!dateString) return "—";
    return moment(dateString).format("h:mm A");
  };

  const formatDuration = (minutes) => {
    if (minutes === undefined || minutes === null) return "—";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  if (loading) {
    return <p style={{ color: "#666", fontStyle: "italic" }}>Loading attendance...</p>;
  }

  if (schedules.length === 0) {
    return <p style={{ color: "#666", fontStyle: "italic" }}>No schedules for today</p>;
  }

  return (
    <div>
      {actionMessage && (
        <p style={{ color: "#374151", fontSize: "13px", marginBottom: "8px" }}>{actionMessage}</p>
      )}
      {schedules.map((schedule) => {
        const attendanceRecord = getAttendanceForSchedule(schedule._id);
        const isCheckedIn = attendanceRecord?.status === "checked-in";
        const isCheckedOut = attendanceRecord?.status === "checked-out";
        const pendingTempCheckout = getActiveTempCheckout(attendanceRecord);

        return (
          <div
            key={schedule._id}
            style={{
              marginBottom: "16px",
              padding: "16px",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              backgroundColor: "#f9fafb",
            }}
          >
            <div style={{ marginBottom: "12px" }}>
              <h3 style={{ margin: "0 0 8px 0", fontSize: "16px", fontWeight: "600" }}>
                {schedule.task}
              </h3>
              <p style={{ margin: "0", fontSize: "14px", color: "#666" }}>
                Expected Duration: {formatDuration(schedule.expectedDuration)}
              </p>
            </div>

            {attendanceRecord ? (
              <div>
                <div style={{ display: "flex", gap: "16px", marginBottom: "8px", flexWrap: "wrap" }}>
                  {attendanceRecord.checkedInAt && (
                    <div>
                      <span style={{ fontSize: "12px", color: "#666" }}>Checked In: </span>
                      <span style={{ fontWeight: "500" }}>{formatTime(attendanceRecord.checkedInAt)}</span>
                    </div>
                  )}
                  {attendanceRecord.checkedOutAt && (
                    <div>
                      <span style={{ fontSize: "12px", color: "#666" }}>Checked Out: </span>
                      <span style={{ fontWeight: "500" }}>{formatTime(attendanceRecord.checkedOutAt)}</span>
                    </div>
                  )}
                </div>
                {attendanceRecord.actualDuration !== undefined && (
                  <div style={{ marginBottom: "8px" }}>
                    <span style={{ fontSize: "12px", color: "#666" }}>Actual Duration: </span>
                    <span style={{ fontWeight: "500" }}>{formatDuration(attendanceRecord.actualDuration)}</span>
                  </div>
                )}
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                  <div
                    style={{
                      display: "inline-block",
                      padding: "4px 12px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: "500",
                      backgroundColor: isCheckedIn ? "#FFEDD5" : "#D1FAE5",
                      color: isCheckedIn ? "#9A3412" : "#065F46",
                    }}
                  >
                    {isCheckedIn ? "○ Checked In" : isCheckedOut ? "✓ Checked Out" : "Pending"}
                  </div>
                  {isCheckedIn && (
                    <button
                      onClick={() => handleCheckOut(attendanceRecord._id)}
                      disabled={loading}
                      className="id-generator__actions-register"
                      style={{
                        opacity: loading ? 0.5 : 1,
                        cursor: loading ? "not-allowed" : "pointer",
                        backgroundColor: "#DC2626",
                        color: "#FFFFFF",
                        padding: "6px 12px",
                        fontSize: "12px",
                      }}
                    >
                      {loading ? "Processing..." : "Check Out"}
                    </button>
                  )}
                  {isCheckedIn && !pendingTempCheckout && (
                    <button
                      onClick={() => {
                        setSelectedAttendance(attendanceRecord);
                        setShowTempCheckoutModal(true);
                        setActionMessage("");
                      }}
                      disabled={loading}
                      className="id-generator__actions-register"
                      style={{
                        opacity: loading ? 0.5 : 1,
                        cursor: loading ? "not-allowed" : "pointer",
                        backgroundColor: "#F59E0B",
                        color: "#FFFFFF",
                        padding: "6px 12px",
                        fontSize: "12px",
                      }}
                    >
                      {loading ? "Processing..." : "Temp Checkout"}
                    </button>
                  )}
                  {pendingTempCheckout && (
                    <button
                      onClick={() => handleTempCheckin(attendanceRecord._id, pendingTempCheckout._id)}
                      disabled={loading}
                      className="id-generator__actions-register"
                      style={{
                        opacity: loading ? 0.5 : 1,
                        cursor: loading ? "not-allowed" : "pointer",
                        backgroundColor: "#2563EB",
                        color: "#FFFFFF",
                        padding: "6px 12px",
                        fontSize: "12px",
                      }}
                    >
                      {loading ? "Processing..." : "Return from Temp Checkout"}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                <p style={{ color: "#666", fontStyle: "italic", margin: "0" }}>Not checked in</p>
                <button
                  onClick={() => handleCheckIn(schedule._id)}
                  disabled={loading}
                  className="id-generator__actions-register"
                  style={{
                    opacity: loading ? 0.5 : 1,
                    cursor: loading ? "not-allowed" : "pointer",
                    backgroundColor: "#059669",
                    color: "#FFFFFF",
                    padding: "6px 12px",
                    fontSize: "12px",
                  }}
                >
                  {loading ? "Processing..." : "Check In"}
                </button>
              </div>
            )}
          </div>
        );
      })}
      {showTempCheckoutModal && selectedAttendance && (
        <div
          style={{
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            padding: "16px",
            marginTop: "16px",
            backgroundColor: "#FFF7ED",
          }}
        >
          <h4 style={{ margin: "0 0 12px 0" }}>Temporary Checkout</h4>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <input
              type="text"
              placeholder="Errand name"
              value={tempCheckoutData.errandName}
              onChange={(e) =>
                setTempCheckoutData((prev) => ({ ...prev, errandName: e.target.value }))
              }
              style={{
                padding: "8px 10px",
                borderRadius: "6px",
                border: "1px solid #D1D5DB",
                flex: "1 1 200px",
              }}
            />
            <input
              type="text"
              placeholder="Reason"
              value={tempCheckoutData.reason}
              onChange={(e) =>
                setTempCheckoutData((prev) => ({ ...prev, reason: e.target.value }))
              }
              style={{
                padding: "8px 10px",
                borderRadius: "6px",
                border: "1px solid #D1D5DB",
                flex: "1 1 200px",
              }}
            />
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={handleTempCheckout}
                disabled={loading}
                className="id-generator__actions-register"
                style={{
                  opacity: loading ? 0.5 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                  backgroundColor: "#F59E0B",
                  color: "#FFFFFF",
                  padding: "8px 14px",
                  fontSize: "12px",
                }}
              >
                {loading ? "Processing..." : "Submit"}
              </button>
              <button
                onClick={() => {
                  setShowTempCheckoutModal(false);
                  setSelectedAttendance(null);
                  setTempCheckoutData({ errandName: "", reason: "" });
                }}
                className="id-generator__actions-register"
                style={{
                  backgroundColor: "#E5E7EB",
                  color: "#374151",
                  padding: "8px 14px",
                  fontSize: "12px",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const User = () => {
  const pathname = useParams();
  const location = useLocation();
  const auth = useSelector((state) => state.auth);
  const role = auth?.userInfo?.role;
  const isAdminLike = role === "admin" || role === "super-admin";
  console.log("auth", auth);

  const navigate = useNavigate();
  
  // Handle both regular :id param and splat route * param
  const userId = pathname?.id || pathname?.['*'];
  
  // Check if viewing a staff profile
  // Also check if user role is staff (they should always see staff view)
  const searchParams = new URLSearchParams(location.search);
  const isStaff = searchParams.get("type") === "staff" || role === "staff";
  const [image, setImage] = useState("");
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [imageLoading, setImageLoading] = useState(false);
  const [mealStatus, setMealStatus] = useState(null);
  const [subject, setSubject] = useState("");
  const [modalState, setModalState] = useState({
    isOpen: false,
    message: "",
    success: false,
  });
  const [attendanceModalState, setAttendanceModalState] = useState({
    isOpen: false,
    message: "",
    success: false,
  });
  const [tabletModalState, setTabletModalState] = useState({
    isOpen: false,
    message: "",
    success: false,
  });
  const [open, setOpen] = useState(false);
  const [tabCheckingOpen, setTabCheckingOpen] = useState(false);
  const [campActionLoading, setCampActionLoading] = useState(false);
  const [mealHistory, setMealHistory] = useState([]);
  const [mealHistoryLoading, setMealHistoryLoading] = useState(false);
  const [mealHistoryModalOpen, setMealHistoryModalOpen] = useState(false);
  const [accommodationModalOpen, setAccommodationModalOpen] = useState(false);
  const [accommodationRooms, setAccommodationRooms] = useState([]);
  const [accommodationRoomsLoading, setAccommodationRoomsLoading] = useState(false);
  const [accommodationRoomsError, setAccommodationRoomsError] = useState("");
  const [accommodationStartDate, setAccommodationStartDate] = useState("");
  const [accommodationActionLoading, setAccommodationActionLoading] = useState(false);
  const [accommodationLogs, setAccommodationLogs] = useState([]);
  const [checkoutEndDate, setCheckoutEndDate] = useState("");
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [accommodationLogsLoading, setAccommodationLogsLoading] = useState(false);
  const [infants, setInfants] = useState([]);
  const [infantsLoading, setInfantsLoading] = useState(false);
  const [crecheActionLoading, setCrecheActionLoading] = useState(false);

  // Role-based section visibility helpers
  const canSeeCampSection = !isStaff && (isAdminLike || role === "camp-checkin");
  const canSeeMealSection = !isStaff && (isAdminLike || role === "food-checker");
  const canSeeAttendanceSection = !isStaff && (isAdminLike || role === "attendance");
  const canSeeTabletSection = !isStaff && (isAdminLike || role === "tab-checking");
  const canSeeAccommodationTimelineSection = isAdminLike || role === "accommodation" || role === "staff";
  const canSeeHallSection = !isStaff && (isAdminLike || role === "attendance" || role === "accommodation");
  const canSeeCrecheSection = !isStaff && (isAdminLike || role === "creche");
  // Staff can see accommodation section when viewing their own profile
  const canSeeAccommodationSection = (
    !(isStaff && role === "staff-attendance") && (isAdminLike || role === "accommodation")
  ) || (role === "staff" && isStaff);

  const mediaRef = useRef(null);
  const tabletMarkedRef = useRef(false);
    useEffect(() => {
    console.log("modalState", modalState);
  }, [modalState]);

  useEffect(() => {
    const hash = window.location.hash;
    const cardId = hash.substring(1);

    if (cardId) {
      const cardElement = document.getElementById(cardId);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [pathname]);

  useEffect(() => {
    // Redirect to Google Play Store only if visiting /admin/view-user/:id
    if (location.pathname.includes("/admin/view-user/")) {
      window.location.href = "https://play.google.com/store/apps/details?id=com.tractrac.plus";
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!auth?.token) {
      navigate(`/login/${userId}`);
    }
  }, []);

  // Staff users should only access their own profile
  useEffect(() => {
    if (auth?.userInfo?.role === "staff") {
      const staffId = auth?.userInfo?.id;
      // If viewing a different profile, redirect to their own
      if (userId && pathname.id !== staffId) {
        navigate(`/staff-profile/${staffId}`);
      }
    }
  }, [auth, pathname, navigate]);

  useEffect(() => {
    if (auth?.userInfo?.role === "health") {
      navigate(`/health/user/${userId}`);
    }
  }, []);
  const attendanceRecordedRef = useRef(false);
  
  // Auto-record attendance when attendance role views user profile (single action)
  useEffect(() => {
    if (
      auth?.userInfo?.role === "attendance" && 
      user?._id && 
      !isStaff && 
      user?.hall && 
      !loading &&
      !attendanceRecordedRef.current
    ) {
      attendanceRecordedRef.current = true;
      setTimeout(() => {
        handleRecordAttendance();
      }, 100);
    }
    
    return () => {
      if (!user?._id) {
        attendanceRecordedRef.current = false;
      }
    };
  }, [user?._id, user?.hall, auth?.userInfo?.role, isStaff, loading]);

  useEffect(() => {
    if (auth?.userInfo?.role === "tab-checking" && user && !isStaff && user._id) {
      // Auto-mark tablet as collected if not already marked
      if (!user.tabletCollected && !tabletMarkedRef.current) {
        tabletMarkedRef.current = true;
        handleMarkTabletCollected();
      } else if (user.tabletCollected && !tabletMarkedRef.current) {
        // Just show the modal with status if already collected
        showTabCheckingModal();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, user?.tabletCollected, auth?.userInfo?.role, isStaff]);
  const handleGetUser = async () => {
    setLoading(true);
    
    // Determine if this is a staff profile
    // Check if user role is staff OR if type=staff query param exists
    const isStaffProfile = auth?.userInfo?.role === "staff" || isStaff;
    
    if (isStaffProfile) {
      // For staff, use their own ID from token if available
      const staffId = auth?.userInfo?.role === "staff" 
        ? (userId || auth?.userInfo?.id)
        : userId;
      
      const response = await getStaff(staffId, auth?.token);
      
      // Map staff data to user format for compatibility
      if (response?.status === 200) {
        const staffData = response?.data?.data || response?.data;
        setUser({
          ...staffData,
          names: staffData.name,
          isStaff: true,
        });
      }
    } else {
    const response = await getUser(userId);
    console.log("getUser", response);

    setImage(response?.data?.photo);
    setUser(response?.data);
    setQrCodeUrl(response?.data?.qrCodeUrl);
    if (response?.status === 200) {
      if (auth?.userInfo?.role === "food-checker") {
      handleRecordMeal(response?.data?._id);
        }
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    handleGetUser();
  }, [isStaff, auth?.userInfo?.role]);

  const loadAccommodationLogs = async () => {
    if (!auth?.token || !userId) return;
    setAccommodationLogsLoading(true);
      try {
        const response = await getUserAccommodationLogs(userId, auth?.token);
        console.log("getUserAccommodationLogs", response);
        
        if (response?.status === 200) {
          setAccommodationLogs(response?.data?.data || []);
        }
    } catch (error) {
      console.error("Error loading accommodation logs:", error);
    } finally {
      setAccommodationLogsLoading(false);
    }
  };

  useEffect(() => {
    if (userId && auth?.token) {
      loadAccommodationLogs();
    }
  }, [userId, auth?.token, user?._id]);

  const loadInfants = async () => {
    if (!auth?.token || !userId || isStaff) return;
    setInfantsLoading(true);
    try {
      const response = await getInfantsByMsp(userId, auth?.token);
      if (response?.status === 200 && response?.data?.success) {
        setInfants(response?.data?.data || []);
      }
    } catch (error) {
      console.error("Error loading infants:", error);
    } finally {
      setInfantsLoading(false);
    }
  };

  useEffect(() => {
    if (
      userId &&
      auth?.token &&
      !isStaff &&
      (auth?.userInfo?.role === "creche" ||
        auth?.userInfo?.role === "admin" ||
        auth?.userInfo?.role === "super-admin")
    ) {
      loadInfants();
    }
  }, [userId, auth?.token, isStaff, auth?.userInfo?.role]);

  const handleCheckInInfant = async (infantId) => {
    if (!auth?.token) {
      showModal("You must be logged in", false);
      return;
    }

    setCrecheActionLoading(true);
    try {
      const response = await checkInInfant(infantId, auth?.token);
      if (response?.status === 200 || response?.data?.success) {
        showModal(response?.data?.message || "Infant checked in successfully", true);
        await loadInfants(); // Refresh infants list
      } else {
        showModal(response?.data?.message || "Unable to check in infant", false);
      }
    } catch (error) {
      console.error("Error checking in infant:", error);
      showModal("An error occurred while checking in infant", false);
    } finally {
      setCrecheActionLoading(false);
    }
  };

  const handleCheckOutInfant = async (infantId) => {
    if (!auth?.token) {
      showModal("You must be logged in", false);
      return;
    }

    setCrecheActionLoading(true);
    try {
      const response = await checkOutInfant(infantId, auth?.token);
      if (response?.status === 200 || response?.data?.success) {
        showModal(response?.data?.message || "Infant checked out successfully", true);
        await loadInfants(); // Refresh infants list
      } else {
        showModal(response?.data?.message || "Unable to check out infant", false);
      }
    } catch (error) {
      console.error("Error checking out infant:", error);
      showModal("An error occurred while checking out infant", false);
    } finally {
      setCrecheActionLoading(false);
    }
  };

  const handleRecordMeal = async (userId) => {
    setLoading(true);
    const response = await recordMeal(userId);
    console.log("recordMeal", response);

    if (response.status === 200) {
      showModal(response?.data?.message, true);
      const response2 = await getUser(userId);
      setImage(response2?.data?.photo);
      setUser(response2?.data);
      setQrCodeUrl(response2?.data?.qrCodeUrl);
    } else {
      showModal(response?.data?.message, false);
    }
    setLoading(false);
  };

  const handleRecordAttendance = async () => {
    if (!auth?.token || !userId) {
      showAttendanceModal("Unable to record attendance", false);
      return;
    }

    setLoading(true);
    try {
      const response = await recordAttendance(userId, null);
      if (response?.status === 200) {
        showAttendanceModal(response?.data?.message || "Attendance marked successfully", true);
        attendanceRecordedRef.current = true;
        await handleGetUser();
      } else {
        showAttendanceModal(response?.data?.message || "Unable to record attendance", false);
      }
    } catch (error) {
      console.error("Error recording attendance:", error);
      showAttendanceModal("An error occurred while recording attendance", false);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkTabletCollected = async () => {
    if (!userId || isStaff || !auth?.token) return;
    
    setLoading(true);
    try {
      const response = await markTabletCollected(userId, auth?.token);
      console.log("markTabletCollected", response);

      if (response?.status === 200) {
        await handleGetUser();
        closeTabCheckingModal(); // Close modal after successful marking
        showTabletModal(response?.data?.message || "Tablet marked as collected successfully", true);
      } else {
        showTabletModal(response?.data?.error || response?.error, false);
      }
    } catch (error) {
      console.error("Error marking tablet as collected:", error);
      showTabletModal("An error occurred while marking tablet as collected", false);
    } finally {
      setLoading(false);
    }
  };

  const getTodayMeals = () => {
    if (!user?.mealRecords) return null;

    const today = getGMT1StartOfDay();

    return user.mealRecords.find(
      (record) => isSameGMT1Date(record.date, today)
    );
  };

  const todayMeals = getTodayMeals();

  const closeModal = () => {
    setModalState((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };
  const closeAttendanceModal = () => {
    setAttendanceModalState((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };
  const closeTabletModal = () => {
    setTabletModalState((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  const showModal = (message, success) => {
    setModalState({
      isOpen: true,
      message,
      success,
    });

    // setTimeout(() => {
    //   closeModal();
    // }, 3000);
  };
  const showAttendanceModal = (message, success) => {
    setAttendanceModalState({
      isOpen: true,
      message,
      success,
    });
  };
  const showTabletModal = (message, success) => {
    setTabletModalState({
      isOpen: true,
      message,
      success,
    });
  };

  const showClassSelectModal = () => {

    setOpen(true);
  };
  const showTabCheckingModal = () => {
    setTabCheckingOpen(true);
  };

  const closeTabCheckingModal = () => {
    setTabCheckingOpen(false);
  };

  const getCurrentMealType = () => {
    const currentHour = new Date().getHours();
    if (currentHour >= 6 && currentHour < 11) return "breakfast";
    if (currentHour >= 11 && currentHour < 16) return "lunch";
    if (currentHour >= 16 && currentHour < 22) return "dinner";
    return "outside meal hours";
  };
  const currentMeal = getCurrentMealType();
  const campCheckIns = Array.isArray(user?.campCheckIns)
    ? user.campCheckIns
    : [];
  const latestCampCheckIn =
    campCheckIns.length > 0 ? campCheckIns[campCheckIns.length - 1] : null;
  const campCheckOuts = Array.isArray(user?.campCheckOuts)
    ? user.campCheckOuts
    : [];
  const latestCampCheckOut =
    campCheckOuts.length > 0
      ? campCheckOuts[campCheckOuts.length - 1]
      : null;
  const dailyCheckIns = Array.isArray(user?.dailyCheckIns)
    ? user.dailyCheckIns
    : [];
  const hasActiveCampCheckIn =
    !!latestCampCheckIn &&
    (!latestCampCheckOut ||
      new Date(latestCampCheckOut.checkedOutAt) <
        new Date(latestCampCheckIn.checkedInAt));

  const isDecamped = latestCampCheckOut?.type === "decamp";
  const currentCampStatus = isDecamped
    ? "decamp"
    : hasActiveCampCheckIn
      ? "in"
      : "out";
  const currentCampStatusText =
    currentCampStatus === "decamp"
      ? "Decamped"
      : currentCampStatus === "in"
        ? "Checked In"
        : "Checked Out";
  const canPerformCampCheckIn = ["camp-checkin", "admin", "super-admin"].includes(
    auth?.userInfo?.role
  );
  const canManageAccommodation = ["accommodation", "admin", "super-admin"].includes(
    auth?.userInfo?.role
  );
  // Staff can checkout themselves from their own profile
  const canSelfCheckout = role === "staff" && isStaff && user?._id === auth?.userInfo?.id;

  // Handle both populated room object and room ID reference
  const accommodationRoom = user?.room ? (
    typeof user.room === 'object' && user.room.name 
      ? user.room 
      : null
  ) : null;
  
  // If room is just an ID, try to find it from accommodationRooms
  const accommodationRoomFromList = !accommodationRoom && user?.room
    ? accommodationRooms.find(r => r._id === (typeof user.room === 'string' ? user.room : user.room?._id))
    : null;
  
  const finalAccommodationRoom = accommodationRoom || accommodationRoomFromList || null;
  const accommodationAssignments = Array.isArray(finalAccommodationRoom?.assignments)
    ? finalAccommodationRoom.assignments
    : [];
  const accommodationCapacity = Number(finalAccommodationRoom?.totalSlots) || 0;
  const accommodationOccupied = accommodationAssignments.length;
  const accommodationAvailable = Math.max(accommodationCapacity - accommodationOccupied, 0);
  const accommodationAssignmentsToShow = accommodationAssignments.slice(0, 6);
  const accommodationExtraOccupants = Math.max(
    accommodationOccupied - accommodationAssignmentsToShow.length,
    0
  );

  const performCampAction = async (action) => {
    if (!auth?.token) {
      showModal("You must be logged in to perform this action.", false);
      return;
    }

    if (action === "checkout" && !latestCampCheckIn) {
      showModal("MSP has not checked in to camp and cannot be checked out.", false);
      return;
    }

    setCampActionLoading(true);

    let response;
    if (action === "checkin") {
      response = await checkInCamp(userId, auth?.token);
    } else if (action === "decamp") {
      response = await decampCamp(userId, auth?.token);
    } else {
      response = await checkOutCamp(userId, auth?.token);
    }

    const successMessages = {
      checkin: "Camp check-in recorded.",
      checkout: "Camp check-out recorded.",
      decamp: "MSP decamped. They will be eligible to return in a subsequent cohort.",
    };
    const errorMessages = {
      checkin: "Unable to check in to camp.",
      checkout: "Unable to check out of camp.",
      decamp: "Unable to decamp MSP.",
    };

    if (response?.status === 200) {
      showModal(
        response?.data?.message || successMessages[action],
        true
      );
      await handleGetUser();
    } else {
      alert(response?.data?.message);
      showModal(
        response?.data?.message || errorMessages[action],
        false
      );
    }

    setCampActionLoading(false);
  };

  const loadAccommodationRooms = async () => {
    if (!auth?.token) {
      showModal("You must be logged in to manage accommodation.", false);
      return;
    }

    setAccommodationRoomsLoading(true);
    setAccommodationRoomsError("");
    try {
      const response = await getAccommodationRooms(auth?.token);
      if (response?.status === 200) {
        setAccommodationRooms(response?.data?.data || []);
      } else {
        setAccommodationRoomsError(
          response?.data?.message || "Unable to load rooms at this time."
        );
        setAccommodationRooms([]);
      }
    } catch (error) {
      console.error("Unable to load accommodation rooms", error);
      setAccommodationRoomsError("Unable to load rooms. Please try again.");
      setAccommodationRooms([]);
    } finally {
      setAccommodationRoomsLoading(false);
    }
  };

  // Load accommodation rooms when user is loaded (for admins only - staff get room data from their profile)
  useEffect(() => {
    if (user?._id && canManageAccommodation) {
      loadAccommodationRooms();
    }
  }, [user?._id, canManageAccommodation]);

  const openAccommodationModalHandler = async () => {
    if (!canManageAccommodation) return;
    setAccommodationModalOpen(true);
    await loadAccommodationRooms();
  };

  const closeAccommodationModal = () => {
    setAccommodationModalOpen(false);
    setAccommodationRoomsError("");
    setAccommodationStartDate("");
  };

  const handleAssignAccommodation = async (roomId) => {
    if (!auth?.token || !user?._id) {
      showModal("Unable to assign room at this time.", false);
      return;
    }

    const currentRoomId = finalAccommodationRoom?._id 
      ? (typeof finalAccommodationRoom._id === 'string' ? finalAccommodationRoom._id : finalAccommodationRoom._id.toString())
      : (user?.room && typeof user.room === 'string' ? user.room : user?.room?._id?.toString());
    if (currentRoomId && currentRoomId === roomId) {
      showModal(`${isStaff ? "Staff" : "MSP"} is already assigned to this room.`, false);
      return;
    }

    setAccommodationActionLoading(true);
    try {
      const response = await updateAccommodationBunk(
        roomId,
        {
          occupantId: user._id,
          occupantName: isStaff ? user.name : user.names,
          occupantType: isStaff ? "Staff" : "User",
          startDate: accommodationStartDate || undefined,
        },
        auth?.token
      );

      if (response?.status === 200) {
        showModal("Accommodation updated successfully.", true);
        await handleGetUser();
        await loadAccommodationRooms();
        await loadAccommodationLogs();
        setAccommodationModalOpen(false);
        setAccommodationStartDate("");
      } else {
        showModal(response?.data?.message || "Unable to assign room.", false);
      }
    } catch (error) {
      console.error("Unable to assign accommodation", error);
      showModal("Unable to assign room. Please try again.", false);
    } finally {
      setAccommodationActionLoading(false);
    }
  };

  const handleCheckoutAccommodation = async (endDate = null) => {
    if (!auth?.token || !user?._id) {
      showModal("Unable to checkout from room.", false);
      return;
    }

    if (!user?.room && !finalAccommodationRoom?._id) {
      showModal(`${isStaff ? "Staff" : "MSP"} is not currently assigned to a room.`, false);
      return;
    }

    setAccommodationActionLoading(true);
    try {
      const roomIdToUse = finalAccommodationRoom?._id 
        ? (typeof finalAccommodationRoom._id === 'string' ? finalAccommodationRoom._id : finalAccommodationRoom._id.toString())
        : (user?.room && typeof user.room === 'string' ? user.room : (user?.room?._id?.toString() || null));
      
      if (!roomIdToUse) {
        showModal("Unable to determine room ID.", false);
        return;
      }

      const response = await updateAccommodationBunk(
        roomIdToUse,
        {
          occupantId: user._id,
          status: "checkout",
          occupantType: isStaff ? "Staff" : "User",
          endDate: endDate || undefined,
        },
        auth?.token
      );

      if (response?.status === 200) {
        const accommodationCost = response?.data?.data?.accommodationCost;
        
        if (accommodationCost && canManageAccommodation) {
          const formattedCost = new Intl.NumberFormat('en-NG', {
            style: 'currency',
            currency: 'NGN',
            minimumFractionDigits: 2,
          }).format(accommodationCost.totalCost);
          
          const formattedPerNight = new Intl.NumberFormat('en-NG', {
            style: 'currency',
            currency: 'NGN',
            minimumFractionDigits: 2,
          }).format(accommodationCost.costPerNight);
          
          const successMessage = (
            <div>
              <p style={{ marginBottom: "12px" }}>
                Checked out successfully.
              </p>
              <div style={{ 
                padding: "12px", 
                backgroundColor: "#f0f9ff", 
                borderRadius: "6px",
                border: "1px solid #bae6fd",
                marginTop: "12px"
              }}>
                <div style={{ fontWeight: "600", marginBottom: "8px", color: "#0369a1" }}>
                  Accommodation Cost
                </div>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#0c4a6e", marginBottom: "4px" }}>
                  {formattedCost}
                </div>
                <div style={{ fontSize: "13px", color: "#64748b" }}>
                  {accommodationCost.nights} night{accommodationCost.nights !== 1 ? 's' : ''} × {formattedPerNight} per night
                </div>
              </div>
            </div>
          );
          showModal(successMessage, true);
        } else {
          showModal("Checked out successfully.", true);
        }
        await handleGetUser();
        await loadAccommodationLogs();
        if (accommodationModalOpen) {
          await loadAccommodationRooms();
        }
        setCheckoutModalOpen(false);
        setCheckoutEndDate("");
      } else {
        showModal(response?.data?.message || "Unable to checkout.", false);
      }
    } catch (error) {
      console.error("Unable to checkout from room", error);
      showModal("Unable to checkout. Please try again.", false);
    } finally {
      setAccommodationActionLoading(false);
    }
  };

  const handleConfirmCheckout = () => {
    handleCheckoutAccommodation(checkoutEndDate || null);
  };

  // Calculate cost estimate for display
  const calculateCostEstimate = () => {
    if (!canManageAccommodation || !finalAccommodationRoom?.cost || !user?.accommodationAssignedAt) {
      return null;
    }

    const assignedDate = new Date(user.accommodationAssignedAt);
    assignedDate.setHours(0, 0, 0, 0);
    
    const checkoutDate = checkoutEndDate 
      ? new Date(checkoutEndDate + "T00:00:00") // Use date only
      : new Date();
    checkoutDate.setHours(0, 0, 0, 0);
    
    // Calculate number of nights based on calendar days
    const timeDiff = checkoutDate.getTime() - assignedDate.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    // Number of nights = number of calendar days (check in Jan 1, checkout Jan 2 = 1 night)
    // But ensure at least 1 night if same day checkout (minimum charge)
    const nights = Math.max(1, daysDiff);
    
    return {
      costPerNight: finalAccommodationRoom.cost,
      nights: nights,
      totalCost: finalAccommodationRoom.cost * nights,
      assignedDate: assignedDate,
      checkoutDate: checkoutDate,
    };
  };

  const fetchMealHistory = async () => {
    if (!auth?.token) {
      showModal("You must be logged in to view meal history.", false);
      return;
    }

    setMealHistoryLoading(true);
    try {
      const response = await getMealHistory({
        userId: userId,
        token: auth?.token,
      });

      if (response?.status === 200) {
        setMealHistory(response?.data?.data?.mealRecords || []);
        setMealHistoryModalOpen(true);
      } else {
        showModal(response?.data?.message || "Unable to fetch meal history.", false);
      }
    } catch (error) {
      showModal("Unable to fetch meal history.", false);
    } finally {
      setMealHistoryLoading(false);
    }
  };

  const closeMealHistoryModal = () => {
    setMealHistoryModalOpen(false);
  };

  const handleViewClick = () => {
    setOpen(false)
    navigate(`/admin/user/${userId}#attendance`);
  }

  const handleGenerateUserId = async () => {
    if (!user?._id || !auth?.token) {
      showModal("Unable to generate user ID at this time.", false);
      return;
    }

    if (!window.confirm("Are you sure you want to generate a user ID for this user?")) {
      return;
    }

    setLoading(true);
    try {
      const response = await generateUserId(user._id);
      if (response?.status === 200 || response?.status === 201) {
        if (response?.data?.qrCodeUrl) {
          setQrCodeUrl(response.data.qrCodeUrl);
        }
        showModal(
          response?.data?.message || "User ID generated successfully!",
          true
        );
        await handleGetUser(); // Refresh user data
      } else {
        showModal(
          response?.data?.message || "Unable to generate user ID.",
          false
        );
      }
    } catch (error) {
      console.error("Error generating user ID:", error);
      showModal("An error occurred while generating user ID.", false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="id-generator">
        <div className="id-generator__container">
          <div className="id-generator__header">
            {!loading&& <>
          {canPerformCampCheckIn && !isStaff && (
                <span
                  className={`camp-status-badge camp-status-badge--${
                    currentCampStatus === "in" ? "in" : "out"
                  }`}
                >
                  {currentCampStatusText}
                </span>
              )}
            </>}
            <h2 className="id-generator__header-title">{user?.names}</h2>
            <div className="id-generator__header-subtitle-container">
              <p className="id-generator__header-subtitle">
                {isStaff ? user?.company : (user?.userId || "No User ID")}
              </p>
              {!isStaff && !user?.userId && isAdminLike && (
                <button
                  className="id-generator__generate-btn"
                  onClick={handleGenerateUserId}
                  disabled={loading}
                  title="Generate User ID"
                >
                  {loading ? "Generating..." : "Generate User ID"}
                </button>
              )}
            </div>
          </div>
             
            

          {user && (
            <div className="id-generator__content">
              {!isStaff && (
                <>
              <div className="id-generator__photo">
                <div className="id-generator__photo-preview">
                  <div className="id-generator__photo-preview-container">
                    {/* {image && ( */}

                    <div className="id-generator__photo-preview">
                      <div className="id-generator__photo-preview-container">
                        {!image && !loading && <div>User has no image</div>}
                        <img
                          src={image}
                          alt=""
                          className="id-generator__photo-preview-image"
                        />
                      </div>
                      {imageLoading && (
                        <div className="id-generator__photo-preview-loading">
                          <div className="id-generator__photo-preview-loading-spinner" />
                        </div>
                      )}
                    </div>
                    {/* )} */}
                  </div>
                </div>
              </div>

              <div className="fingerprint-grid">
                <div className="id-generator__photo">
                  <div className="id-generator__photo-preview">
                    <div className="id-generator__photo-preview-container">
                      {!user?.leftFingerPrint && !loading && (
                        <div>No left finger print</div>
                      )}

                      <div className="id-generator__photo-preview">
                        <div className="id-generator__photo-preview-container">
                          <img
                            src={user?.leftFingerPrint}
                            alt=""
                            className="id-generator__photo-preview-image"
                          />
                        </div>
                        {imageLoading && (
                          <div className="id-generator__photo-preview-loading">
                            <div className="id-generator__photo-preview-loading-spinner" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="id-generator__photo">
                  <div className="id-generator__photo-preview">
                    <div className="id-generator__photo-preview-container">
                      <div className="id-generator__photo-preview">
                        <div className="id-generator__photo-preview-container">
                          {!user?.rightFingerPrint && !loading && (
                            <div>No right finger print</div>
                          )}
                          <img
                            src={user?.rightFingerPrint}
                            alt=""
                            className="id-generator__photo-preview-image"
                          />
                        </div>
                        {imageLoading && (
                          <div className="id-generator__photo-preview-loading">
                            <div className="id-generator__photo-preview-loading-spinner" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </>
              )}

        {canSeeCampSection && (
        <div className="meal-status" id="camp-checkin">
          <h2>Camp Attendance</h2>
          <div className="camp-status-summary">
            <div className="camp-status-summary__item">
              <span className="camp-status-summary__label">Current status</span>
              <span
                className={`camp-status-summary__value camp-status-summary__value--${
                  currentCampStatus === "in" ? "in" : "out"
                }`}
              >
                {currentCampStatusText}
              </span>
            </div>
            <div className="camp-status-summary__item">
              <span className="camp-status-summary__label">Last check-in</span>
              <span className="camp-status-summary__value">
                {latestCampCheckIn
                  ? `${moment(latestCampCheckIn.checkedInAt).format(
                      "MMMM Do YYYY, h:mm A"
                    )} • Camp Check-In`
                  : "Not yet recorded"}
              </span>
            </div>
            <div className="camp-status-summary__item">
              <span className="camp-status-summary__label">Last check-out</span>
              <span className="camp-status-summary__value">
                {latestCampCheckOut
                  ? `${moment(latestCampCheckOut.checkedOutAt).format(
                      "MMMM Do YYYY, h:mm A"
                    )} • ${latestCampCheckOut?.type === "decamp" ? "Camp Decamp" : "Camp Check-Out"}`
                  : "Not yet recorded"}
              </span>
            </div>
          </div>
           {canPerformCampCheckIn && (
             <div style={{ display: "flex", gap: "12px", marginTop: "16px", flexWrap: "wrap" }}>
               {hasActiveCampCheckIn ? (
                 <>
                   <button
                     className="id-generator__actions-register"
                     onClick={() => performCampAction("checkout")}
                     disabled={campActionLoading || loading}
                     style={{ backgroundColor: "#2B6CB0" }}
                   >
                     {campActionLoading ? "Processing…" : "Start Check-Out"}
                   </button>
                   <button
                     className="id-generator__actions-register"
                     onClick={() => performCampAction("decamp")}
                     disabled={campActionLoading || loading}
                     style={{ backgroundColor: "#C53030" }}
                     title="Remove MSP from cohort early; they will return in a subsequent cohort"
                   >
                     {campActionLoading ? "Processing…" : "Decamp"}
                   </button>
                 </>
               ) : (
                 <>
                   <button
                     className="id-generator__actions-register"
                     onClick={() => performCampAction("checkin")}
                     disabled={campActionLoading || loading}
                   >
                     {campActionLoading ? "Checking in…" : "Start Check-In"}
                   </button>
                   <button
                     className="id-generator__actions-register"
                     onClick={() => performCampAction("decamp")}
                     disabled={campActionLoading || loading}
                     style={{ backgroundColor: "#C53030" }}
                     title="Remove MSP from cohort; they will return in a subsequent cohort"
                   >
                     {campActionLoading ? "Processing…" : "Decamp"}
                   </button>
                 </>
               )}
             </div>
           )}
          {(campCheckIns.length > 0 || campCheckOuts.length > 0) && (
            <div className="camp-timeline">
              <h3 className="camp-timeline__title">Check In/Out Timeline</h3>
              <ul className="camp-timeline__list">
                {[...campCheckIns, ...campCheckOuts]
                  .map((record) => ({
                    type: record.checkedInAt ? "checkin" : "checkout",
                    date: record.checkedInAt || record.checkedOutAt,
                    role: record.performedRole,
                    performedBy: record.performedBy,
                  }))
                  .filter((record) => record.date)
                  .sort(
                    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
                  )
                  .map((record, index) => (
                    <li
                      key={`camp-event-${index}`}
                      className={`camp-timeline__item camp-timeline__item--${record.type}`}
                    >
                      <div className="camp-timeline__icon">
                        {record.type === "checkin" ? "⬆" : "⬇"}
                      </div>
                      <div>
                        <div className="camp-timeline__label">
                          {moment(record.date).format("MMMM Do YYYY, h:mm A")}
                        </div>
                        <div className="camp-timeline__type">
                          {record.type === "checkin"
                            ? "Camp Check-In"
                            : "Camp Check-Out"}
                        </div>
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          )}
        
          {dailyCheckIns.length > 0 && (
            <div className="camp-daily-checkins">
              <h3 className="camp-timeline__title">Daily Check-Ins</h3>
              <ul className="camp-timeline__list">
                {[...dailyCheckIns]
                  .filter((entry) => entry.date)
                  .sort(
                    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
                  )
                  .map((entry, index) => (
                    <li key={`daily-checkin-${index}`} className="camp-timeline__item">
                      <div className="camp-timeline__icon">🗓</div>
                      <div>
                        <div className="camp-timeline__label">
                          {moment(entry.date).format("MMMM Do YYYY")}
                        </div>
                        <div className="camp-timeline__type">
                          Marked present at{" "}
                          {entry.recordedAt
                            ? moment(entry.recordedAt).format("h:mm A")
                            : "—"}
                        </div>
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </div>
              )}

              <div className="id-generator__form-grid">
                {!isStaff ? (
                  <>
                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">User Id</label>
                  <div className="id-generator__form-value">{user?.userId}</div>
                </div>
                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Full Name</label>
                  <div className="id-generator__form-value">{user?.names}</div>
                </div>
                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Tablet Status</label>
                  <div className="id-generator__form-value">
                    {user?.tabletCollected ? (
                      <span style={{ color: '#28a745' }}>
                        ✓ Collected on {moment(user?.tabletCollectedAt).format('MMMM DD, YYYY [at] hh:mm A')}
                      </span>
                    ) : (
                      <span style={{ color: '#dc3545' }}>Not Collected</span>
                    )}
                  </div>
                </div>
                  </>
                ) : (
                  <>
                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Full Name</label>
                  <div className="id-generator__form-value">{user?.names}</div>
                </div>
                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Company</label>
                  <div className="id-generator__form-value">{user?.company}</div>
                </div>
                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Phone Number</label>
                  <div className="id-generator__form-value">{user?.phoneNumber}</div>
                </div>
                  </>
                )}

                {!isStaff && (
                  <>
                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">
                    Credentials
                  </label>
                  <div className="id-generator__form-value">
                    {user?.credentials?.url && (
                      <button
                        className="id-generator__actions-register"
                        onClick={() =>
                          window.open(
                            user?.credentials?.url,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                      >
                        View credentials
                      </button>
                    )}
                  </div>
                </div>
                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Community</label>
                  <div className="id-generator__form-value">
                    {user?.community}
                  </div>
                </div>


                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">LGA</label>
                  <div className="id-generator__form-value">{user?.lga}</div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">State</label>
                  <div className="id-generator__form-value">{user?.state}</div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">
                    Phone Number
                  </label>
                  <div className="id-generator__form-value">
                    {user?.phoneNumber}
                  </div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Date of birth</label>
                  <div className="id-generator__form-value">
                    {user?.dob != null && user?.dob !== ""
                      ? moment(user.dob).isValid()
                        ? moment(user.dob).format("MMMM D, YYYY")
                        : String(user.dob)
                      : "—"}
                  </div>
                </div>
                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Age</label>
                  <div className="id-generator__form-value">{user?.age}</div>
                </div>
                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Msp Type</label>
                  <div className="id-generator__form-value">
                    {user?.mspType}
                  </div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Gender</label>
                  <div className="id-generator__form-value">{user?.gender}</div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Operator</label>
                  <div className="id-generator__form-value">
                    {user?.operator}
                  </div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Languages</label>
                  <div className="id-generator__form-value">
                    {user?.languages}
                  </div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Disability</label>
                  <div className="id-generator__form-value">
                    {user?.disability}
                  </div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Religion</label>
                  <div className="id-generator__form-value">
                    {user?.religion}
                  </div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">
                    Birth Certificate
                  </label>
                  <div className="id-generator__form-value">
                    {user?.birthCertificateCheck}
                  </div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">ID Type</label>
                  <div className="id-generator__form-value">{user?.idType}</div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">ID Number</label>
                  <div className="id-generator__form-value">
                    {user?.idNumber}
                  </div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">
                    Qualification
                  </label>
                  <div className="id-generator__form-value">
                    {user?.qualification}
                  </div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">
                    Physical Fitness
                  </label>
                  <div className="id-generator__form-value">
                    {user?.physicalFitness}
                  </div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">
                    Availability
                  </label>
                  <div className="id-generator__form-value">
                    {user?.availability}
                  </div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">
                    Health Condition
                  </label>
                  <div className="id-generator__form-value">
                    {user?.preExistingHealthCondition}
                  </div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">
                    Nursing Mother
                  </label>
                  <div className="id-generator__form-value">
                    {user?.nursingMother}
                  </div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Allergies</label>
                  <div className="id-generator__form-value">{user?.allergies}</div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Bank Name</label>
                  <div className="id-generator__form-value">{user?.bankName}</div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Account Name</label>
                  <div className="id-generator__form-value">{user?.accountName}</div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Account Number</label>
                  <div className="id-generator__form-value">{user?.accountNumber}</div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Commcare ID</label>
                  <div className="id-generator__form-value">{user?.commcareID}</div>
                </div>

                <div className="id-generator__form-info">
                  <label className="id-generator__form-label">Remark</label>
                  <div className="id-generator__form-value">{user?.remark}</div>
                </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="id-generator__actions">
            <Link
              className="id-generator__actions-download"
              disabled={loading}
              to="/"
            >
              Go back to users
            </Link>
          </div>
        </div>

        {(auth?.userInfo?.role === "food-checker" ||
          auth?.userInfo?.role === "admin" ||
          auth?.userInfo?.role === "super-admin") && (
          <div className="id-display__container">
            {loading ? (
              <div className="loading">Loading...</div>
            ) : (
              <>
                {/* Existing ID card display code */}

                {/* Add meal status section */}
                {!isStaff && (
                <div className="meal-status">
                  <h2>Today's Meals</h2>

                  {todayMeals && (
                    <div className="meal-records">
                      <div className="meal-record">
                        <span>Breakfast:</span>
                        <span
                          className={
                            todayMeals.breakfast ? "recorded" : "not-recorded"
                          }
                        >
                          {todayMeals.breakfast ? "✓" : "×"}
                        </span>
                      </div>
                      <div className="meal-record">
                        <span>Lunch:</span>
                        <span
                          className={
                            todayMeals.lunch ? "recorded" : "not-recorded"
                          }
                        >
                          {todayMeals.lunch ? "✓" : "×"}
                        </span>
                      </div>
                      <div className="meal-record">
                        <span>Dinner:</span>
                        <span
                          className={
                            todayMeals.dinner ? "recorded" : "not-recorded"
                          }
                        >
                          {todayMeals.dinner ? "✓" : "×"}
                        </span>
                      </div>
                      <div className="current-meal-info">
                        Current meal period: {currentMeal}
                      </div>
                    </div>
                  )}
                  <button
                    className="id-generator__actions-register"
                    onClick={fetchMealHistory}
                    disabled={mealHistoryLoading}
                    style={{ marginTop: "16px" }}
                  >
                    {mealHistoryLoading
                      ? "Loading history..."
                      : "View full meal history"}
                  </button>
                </div>
                )}

                <StatusModal
                  isOpen={mealHistoryModalOpen}
                  success
                  onClose={closeMealHistoryModal}
                  title="Full Meal History"
                  message={
                    mealHistory.length === 0 ? (
                      <span>No meal history available.</span>
                    ) : (
                      <div className="meal-history-modal">
                        <table className="meal-history-modal__table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Breakfast</th>
                              <th>Lunch</th>
                              <th>Dinner</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mealHistory.map((record) => (
                              <tr key={record.date}>
                                <td>{moment(record.date).format("MMMM Do YYYY")}</td>
                                <td>{record.breakfast ? "✓" : "×"}</td>
                                <td>{record.lunch ? "✓" : "×"}</td>
                                <td>{record.dinner ? "✓" : "×"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  }
                />
              </>
            )}
          </div>
        )}

       {user && (
        <>


        <TabChecking 
             open={tabCheckingOpen}
             loading={loading}
             tabletCollected={user?.tabletCollected}
             tabletCollectedAt={user?.tabletCollectedAt}
             onMarkCollected={handleMarkTabletCollected}
             onClose={closeTabCheckingModal}
              />
        <StatusModal
              isOpen={modalState.isOpen}
              message={modalState.message}
              success={modalState.success}
              onClose={closeModal}
            />
<StatusModal
              isOpen={attendanceModalState.isOpen}
              message={attendanceModalState.message}
              success={attendanceModalState.success}
              onClose={closeAttendanceModal}
            />
<StatusModal
              isOpen={tabletModalState.isOpen}
              message={tabletModalState.message}
              success={tabletModalState.success}
              onClose={closeTabletModal}
              title="Tablet Collection"
            />
        {canSeeAccommodationTimelineSection && accommodationLogs.length > 0 && (
            <div className="camp-timeline">
              <h3 className="camp-timeline__title">Accommodation Timeline</h3>
              <ul className="camp-timeline__list">
                {accommodationLogs
                  .filter((log) => log.createdAt)
                  .sort(
                    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                  )
                  .map((log, index) => {
                    const actionType = log.action;
                    let icon = "🏠";
                    let label = "";
                    let backdatedDate = null;
                    
                    if (actionType === "accommodation.assign") {
                      icon = "➕";
                      label = `Assigned to room ${log.metadata?.roomName || "Unknown"}`;
                      backdatedDate = log.metadata?.assignedDate;
                    } else if (actionType === "accommodation.checkout") {
                      icon = "🚪";
                      label = `Checked out from room ${log.metadata?.roomName || "Unknown"}`;
                      backdatedDate = log.metadata?.checkoutDate;
                    } else if (actionType === "accommodation.unassign") {
                      icon = "➖";
                      if (log.metadata?.reason === "room_change") {
                        label = `Removed from room ${log.metadata?.roomName || "Unknown"} (room change)`;
                      } else {
                        label = `Checked out from room ${log.metadata?.roomName || "Unknown"}`;
                      }
                      backdatedDate = log.metadata?.checkoutDate;
                    } else if (actionType === "accommodation.change") {
                      icon = "🔄";
                      label = `Moved from room ${log.metadata?.previousRoomName || log.metadata?.previousRoomId || "Unknown"} to room ${log.metadata?.roomName || "Unknown"}`;
                      backdatedDate = log.metadata?.assignedDate;
                    } else {
                      label = log.description || "Accommodation change";
                    }

                    // Check if backdated date differs from log date
                    const logDate = new Date(log.createdAt);
                    const backdatedDateObj = backdatedDate ? new Date(backdatedDate) : null;
                    const isBackdated = backdatedDateObj && 
                      Math.abs(logDate.getTime() - backdatedDateObj.getTime()) > 60000; // More than 1 minute difference

                    // Use backdated date as primary if available, otherwise use log date
                    const displayDate = isBackdated ? backdatedDateObj : logDate;

                    return (
                      <li
                        key={`accommodation-event-${index}`}
                        className={`camp-timeline__item camp-timeline__item--${actionType.replace("accommodation.", "")}`}
                      >
                        <div className="camp-timeline__icon">{icon}</div>
                        <div>
                          <div className="camp-timeline__label">
                            {moment(displayDate).format("MMMM Do YYYY, h:mm A")}
                            {isBackdated && (
                              <span style={{ 
                                marginLeft: "8px", 
                                fontSize: "0.85em", 
                                color: "#666",
                                fontStyle: "italic"
                              }}>
                                (Recorded on {moment(log.createdAt).format("MMM Do, h:mm A")})
                              </span>
                            )}
                          </div>
                          <div className="camp-timeline__type">{label}</div>
                          {log.performedBy && (
                            <div className="camp-timeline__meta" style={{ fontSize: "0.85em", color: "#666", marginTop: "4px" }}>
                              by {typeof log.performedBy === "object" ? log.performedBy.username || "System" : "System"}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
              </ul>
            </div>
          )}
          <br />
          
          {/* Hall Assignment Section */}
        {canSeeHallSection && (
        <div className="meal-status" id="hall-assignment" style={{ marginBottom: "24px" }}>
              <h2>Hall Assignment</h2>
              {user?.hall ? (
                <div className="user-accommodation">
                  <div className="user-accommodation__summary">
                    <div>
                      <span className="user-accommodation__label">Hall</span>
                      <h3 className="user-accommodation__room-name">
                        {user.hall}
              </h3>
                    </div>
                  </div>
                  {user?.hallAssignedAt && (
                    <div className="user-accommodation__assigned-at">
                      Assigned on {moment(user.hallAssignedAt).format("MMMM Do YYYY, h:mm A")}
                    </div>
                  )}
                </div>
              ) : (
                <div className="user-accommodation">
                  <p style={{ color: "#666", fontStyle: "italic" }}>No hall assignment</p>
                </div>
              )}
            </div>
          )}

          {/* Creche Section - Visible for creche, admin, and super-admin roles */}
          {canSeeCrecheSection && (
            <div className="meal-status" id="creche">
              <h2>Creche</h2>
              {infantsLoading ? (
                <div className="loading">Loading infants...</div>
              ) : infants.length === 0 ? (
                <div className="user-accommodation">
                  <p style={{ color: "#666", fontStyle: "italic" }}>
                    No infants enrolled for this MSP
                  </p>
                </div>
              ) : (
                <div className="user-accommodation">
                  {infants.map((infant) => (
                    <div
                      key={infant._id}
                      className="user-accommodation__passenger"
                style={{
                        marginBottom: "16px",
                        padding: "16px",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                        {infant.image ? (
                          <img
                            src={infant.image}
                            alt={infant.name}
                            style={{
                              width: "60px",
                              height: "60px",
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "60px",
                              height: "60px",
                              borderRadius: "50%",
                              backgroundColor: "#E2E8F0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "20px",
                              fontWeight: "bold",
                              color: "#64748B",
                            }}
                          >
                            {getInitialsFromName(infant.name)}
                          </div>
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: "600", fontSize: "16px", marginBottom: "4px" }}>
                            {infant.name}
                          </div>
                          <div style={{ color: "#64748B", fontSize: "14px", marginBottom: "8px" }}>
                            Age: {infant.age}
                          </div>
                          <div
                            style={{
                              display: "inline-block",
                              padding: "4px 12px",
                              borderRadius: "12px",
                              fontSize: "12px",
                              fontWeight: "500",
                              backgroundColor: infant.isCheckedIn ? "#D1FAE5" : "#FFEDD5",
                              color: infant.isCheckedIn ? "#065F46" : "#9A3412",
                            }}
                          >
                            {infant.isCheckedIn ? "✓ Checked In" : "○ Checked Out"}
                          </div>
                          {infant.lastCheckedInAt && (
                            <div style={{ fontSize: "12px", color: "#94A3B8", marginTop: "4px" }}>
                              Last checked in: {moment(infant.lastCheckedInAt).format("MMM Do, h:mm A")}
                            </div>
                          )}
                          {infant.lastCheckedOutAt && !infant.isCheckedIn && (
                            <div style={{ fontSize: "12px", color: "#94A3B8", marginTop: "4px" }}>
                              Last checked out: {moment(infant.lastCheckedOutAt).format("MMM Do, h:mm A")}
                            </div>
                          )}
                        </div>
                        {auth?.userInfo?.role === "creche" && (
                          <div style={{ display: "flex", gap: "8px" }}>
                            {infant.isCheckedIn ? (
                <button
                  className="id-generator__actions-register"
                                onClick={() => handleCheckOutInfant(infant._id)}
                                disabled={crecheActionLoading}
                  style={{
                                  backgroundColor: "#DC2626",
                                  minWidth: "120px",
                  }}
                >
                                {crecheActionLoading ? "Processing..." : "Check Out"}
                </button>
                            ) : (
                <button
                  className="id-generator__actions-register"
                                onClick={() => handleCheckInInfant(infant._id)}
                                disabled={crecheActionLoading}
                                style={{
                                  backgroundColor: "#059669",
                                  minWidth: "120px",
                                }}
                              >
                                {crecheActionLoading ? "Processing..." : "Check In"}
                </button>
                            )}
              </div>
                        )}
            </div>
                    </div>
                  ))}
          </div>
        )}
            </div>
          )}

          {/* Accommodation Section - Hidden for staff-attendance role when viewing staff */}
          {canSeeAccommodationSection && (
        <div className="meal-status" id="accommodation">
          <h2>Accommodation</h2>
          <div className="user-accommodation">
            {finalAccommodationRoom ? (
              <>
                <div className="user-accommodation__summary">
                  <div>
                    <span className="user-accommodation__label">Room</span>
                    <h3 className="user-accommodation__room-name">
                      {finalAccommodationRoom.name || "Room name unavailable"}
                    </h3>
                  </div>
                  <div className="user-accommodation__metrics">
                    <div>
                      <span className="user-accommodation__metric-label">Capacity</span>
                      <span className="user-accommodation__metric-value">
                        {accommodationOccupied}/{accommodationCapacity || "—"}
                      </span>
                    </div>
                    <div>
                      <span className="user-accommodation__metric-label">Available</span>
                      <span
                        className={`user-accommodation__metric-chip user-accommodation__metric-chip--${accommodationAvailable > 0 ? "available" : "full"}`}
                      >
                        {accommodationAvailable}
                      </span>
                    </div>
                  </div>
                </div>
                {user?.accommodationAssignedAt && (
                  <div className="user-accommodation__assigned-at">
                    Assigned on {moment(user.accommodationAssignedAt).format("MMMM Do YYYY, h:mm A")}
                  </div>
                )}
                {finalAccommodationRoom.description && (
                  <div className="user-accommodation__description">
                    {finalAccommodationRoom.description}
                  </div>
                )}
                <div className="user-accommodation__passengers">
                  {accommodationAssignmentsToShow
                    .map((assignment, index) => {
                      const occupant =
                        assignment?.user && typeof assignment.user === "object"
                          ? assignment.user
                          : null;
                      const occupantIdRaw = occupant?._id || assignment.user;
                      const occupantId = occupantIdRaw
                        ? occupantIdRaw.toString()
                        : `occupant-${index}`;
                      const currentUserId = user?._id ? user._id.toString() : "";
                      const occupantName =
                        assignment.userName || occupant?.names || "Assigned MSP";
                      const occupantUserId = occupant?.userId || "—";
                      const occupantContact =
                        occupant?.email || occupant?.phoneNumber || "";
                      const isCurrentUser = occupantId === currentUserId;

                      if (!isCurrentUser) return null;

                      return (
                        <div
                          className={`user-accommodation__passenger ${
                            isCurrentUser
                              ? "user-accommodation__passenger--current"
                              : ""
                          }`}
                          key={`${occupantId}-${index}`}
                        >
                          <div className="user-accommodation__passenger-avatar">
                            {getInitialsFromName(occupantName)}
                          </div>
                          <div className="user-accommodation__passenger-info">
                            <div className="user-accommodation__passenger-name">
                              <span>{occupantName}</span>
                              <span className="user-accommodation__assignment-tag">
                                {occupantUserId}
                              </span>
                              {isCurrentUser && (
                                <span className="user-accommodation__badge">
                                  This {isStaff ? "Staff" : "MSP"}
                                </span>
                              )}
                            </div>
                            {occupantContact && (
                              <span className="user-accommodation__passenger-contact">
                                {occupantContact}
                              </span>
                            )}
                            {assignment?.assignedAt && (
                              <span className="user-accommodation__passenger-time">
                                Assigned {moment(assignment.assignedAt).fromNow()}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
                {accommodationExtraOccupants > 0 && (
                  <div className="user-accommodation__note">
                    +{accommodationExtraOccupants} more occupant{accommodationExtraOccupants === 1 ? "" : "s"} in this room
                  </div>
                )}
                {(canManageAccommodation || canSelfCheckout) && (
                  <div className="user-accommodation__actions">
                    {canManageAccommodation && (
                      <button
                        className="user-accommodation__button"
                        onClick={openAccommodationModalHandler}
                        disabled={accommodationActionLoading}
                      >
                        {accommodationActionLoading ? "Processing…" : "Change room"}
                      </button>
                    )}
                    <button
                      className="user-accommodation__button user-accommodation__button--secondary"
                      onClick={() => setCheckoutModalOpen(true)}
                      disabled={accommodationActionLoading}
                    >
                      {accommodationActionLoading ? "Processing…" : "Checkout"}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="user-accommodation__empty">
                <strong>No accommodation assigned.</strong>
                <span>
                  Assign this {isStaff ? "staff" : "MSP"} to a room to keep track of their accommodation status.
                </span>
                {canManageAccommodation && (
                  <button
                    className="user-accommodation__button"
                    onClick={openAccommodationModalHandler}
                    disabled={accommodationActionLoading}
                  >
                    {accommodationActionLoading ? "Loading…" : "Assign to a room"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
          )}

        {/* Staff Attendance Section - Only for staff profiles and relevant roles */}
        {isStaff && (isAdminLike || role === "staff-attendance") && (
          <div className="meal-status" id="staff-attendance">
            <h2>Staff Attendance</h2>
            {user?._id && (
              <StaffAttendanceSection staffId={user._id} auth={auth} />
            )}
          </div>
        )}
       
        {canSeeAttendanceSection && (
          <>
        <div className="meal-status" id="attendance">
          <h2>Attendance</h2>
          {auth?.userInfo?.role === "attendance" && user?._id && (() => {
            const today = getGMT1StartOfDay();
            const todayRecord = user?.attendance?.find((record) =>
              isSameGMT1Date(record.date, today)
            );
            const isMarked = !!todayRecord;

            return (
              <div style={{ marginBottom: "16px", display: "flex", alignItems: "start", gap: "12px", flexWrap: "wrap" }}>
                <button
                  onClick={() => handleRecordAttendance()}
                  disabled={loading || isMarked}
                  className="id-generator__actions-register"
                  style={{
                    opacity: loading || isMarked ? 0.5 : 1,
                    cursor: loading || isMarked ? "not-allowed" : "pointer",
                    backgroundColor: !isMarked ? "#059669" : "#E5E7EB",
                    color: !isMarked ? "#FFFFFF" : "#6B7280",
                  }}
                >
                  {loading ? "Processing..." : isMarked ? "Already Marked" : "Mark Attendance"}
                </button>
                {todayRecord && (
                  <div style={{ 
                    display: "flex",
                    alignItems: "center",
                    padding: "4px 12px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: "500",
                    backgroundColor: "#D1FAE5",
                    color: "#065F46",
                    marginTop: "8px",
                  }}>
                    ✓ Marked for {todayRecord.subject}
                  </div>
                )}
                      </div>
            );
          })()}
          <div className="meal-records">
            {user?.attendance && user.attendance.length > 0 ? (() => {
              // Group attendance records by date and subject
              const groupedAttendance = {};
              
              user.attendance.forEach((record) => {
                const recordDate = record.date instanceof Date ? record.date : new Date(record.date);
                const dateKey = moment(recordDate).format("YYYY-MM-DD");
                const key = `${dateKey}_${record.subject}`;
                
                if (!groupedAttendance[key]) {
                  groupedAttendance[key] = {
                    date: recordDate,
                    subject: record.subject,
                    records: []
                  };
                }
                groupedAttendance[key].records.push(record);
              });

              // Sort groups by date (newest first), then by subject
              const sortedGroups = Object.values(groupedAttendance).sort((a, b) => {
                const dateA = a.date instanceof Date ? a.date : new Date(a.date);
                const dateB = b.date instanceof Date ? b.date : new Date(b.date);
                if (dateB.getTime() !== dateA.getTime()) {
                  return dateB - dateA;
                }
                return (a.subject || "").localeCompare(b.subject || "");
              });

              // Sort records within each group by check-in time (newest first)
              sortedGroups.forEach(group => {
                group.records.sort((a, b) => {
                  const timeA = a.checkedInAt ? (a.checkedInAt instanceof Date ? a.checkedInAt : new Date(a.checkedInAt)) : new Date(0);
                  const timeB = b.checkedInAt ? (b.checkedInAt instanceof Date ? b.checkedInAt : new Date(b.checkedInAt)) : new Date(0);
                  return timeB - timeA;
                });
              });

              return sortedGroups.map((group, groupIndex) => (
                <div key={groupIndex} style={{ marginBottom: "24px" }}>
                  <div style={{ 
                    fontWeight: "600", 
                    fontSize: "16px", 
                    marginBottom: "12px",
                    color: "#1F2937",
                    paddingBottom: "8px",
                    borderBottom: "2px solid #E2E8F0"
                  }}>
                    {moment(group.date).format("MMMM Do YYYY")} - {group.subject}
                    <span style={{ 
                      fontSize: "12px", 
                      fontWeight: "400", 
                      color: "#6B7280",
                      marginLeft: "8px"
                    }}>
                      ({group.records.length} session{group.records.length !== 1 ? "s" : ""})
                    </span>
                        </div>
                  {group.records.map((item, recordIndex) => {
                    const isCheckedIn = item?.checkedInAt && !item?.checkedOutAt;
                    const isCheckedOut = item?.checkedInAt && item?.checkedOutAt;
                    const sessionNumber = group.records.length - recordIndex; // Show session number (1, 2, 3...)
                    
                    return (
                      <div 
                        key={recordIndex} 
                        className="meal-record" 
                        style={{ 
                          padding: "12px",
                          border: "1px solid #E2E8F0",
                          borderRadius: "8px",
                          marginBottom: "8px",
                          marginLeft: "16px",
                          backgroundColor: isCheckedIn ? "#FFEDD5" : isCheckedOut ? "#F0FDF4" : "#F9FAFB",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                          <span style={{ fontWeight: "500", fontSize: "14px", color: "#4B5563" }}>
                            Session {sessionNumber}
                          </span>
                          <span 
                            style={{
                              display: "inline-block",
                              padding: "4px 12px",
                              borderRadius: "12px",
                              fontSize: "12px",
                              fontWeight: "500",
                              backgroundColor: isCheckedIn ? "#FFEDD5" : isCheckedOut ? "#D1FAE5" : "#F3F4F6",
                              color: isCheckedIn ? "#9A3412" : isCheckedOut ? "#065F46" : "#6B7280",
                            }}
                          >
                            {isCheckedIn ? "○ Checked In" : isCheckedOut ? "✓ Checked Out" : "—"}
                          </span>
                        </div>
                        {item?.checkedInAt && (
                          <div style={{ fontSize: "14px", color: "#6B7280", marginBottom: "4px" }}>
                            Checked in: {moment(item.checkedInAt).format("h:mm A")}
                      </div>
                        )}
                        {item?.checkedOutAt && (
                          <div style={{ fontSize: "14px", color: "#6B7280", marginBottom: "4px" }}>
                            Checked out: {moment(item.checkedOutAt).format("h:mm A")}
            </div>
          )}
                        {isCheckedOut && item?.checkedInAt && item?.checkedOutAt && (
                          <div style={{ fontSize: "12px", color: "#9CA3AF", marginTop: "4px" }}>
                            Duration: {moment(item.checkedOutAt).diff(moment(item.checkedInAt), "minutes")} minutes
                            {moment(item.checkedOutAt).diff(moment(item.checkedInAt), "hours") > 0 && (
                              <span> ({moment(item.checkedOutAt).diff(moment(item.checkedInAt), "hours")} hours)</span>
                            )}
                        </div>
                        )}
                        </div>
                    );
                  })}
                      </div>
              ));
            })() : (
              <p style={{ color: "#666", fontStyle: "italic" }}>No attendance records</p>
            )}
          </div>
        </div>
        {canSeeTabletSection && (
        <div className="meal-status" id="tablet">
          <h2>Tablet</h2>
          <div className="meal-records">
            {user?.tabletCollected ? (
              <div className="meal-record">
                <span>
                  Tablet collected on {moment(user?.tabletCollectedAt).format("MMMM Do YYYY, h:mm A")}
                </span>
                <span className="recorded">✓</span>
              </div>
            ) : (
              <div className="meal-record">
                <span>Tablet not yet collected</span>
                <span style={{ color: '#dc3545' }}>✗</span>
              </div>
            )}
          </div>
        </div>
        )}
          </>
        )}
        {accommodationModalOpen && (
          <div className="modal-overlay">
            <div className="user-accommodation-modal">
              <div className="user-accommodation-modal__header">
                <h3>Select Room</h3>
                <button
                  type="button"
                  className="user-accommodation-modal__close"
                  onClick={closeAccommodationModal}
                  aria-label="Close accommodation assignment modal"
                >
                  ×
                </button>
              </div>
              <div style={{ padding: "16px", borderBottom: "1px solid #e0e0e0" }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", fontSize: "14px" }}>
                  Start Date (Optional - for backdating):
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="date"
                    value={accommodationStartDate}
                    onChange={(e) => setAccommodationStartDate(e.target.value)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      fontSize: "14px",
                      flex: 1,
                      maxWidth: "300px",
                    }}
                    title="Leave empty to use today's date, or select a date to backdate the assignment"
                  />
                  {accommodationStartDate && (
                    <button
                      type="button"
                      onClick={() => setAccommodationStartDate("")}
                      style={{
                        padding: "6px 12px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        backgroundColor: "#f5f5f5",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              {accommodationRoomsLoading ? (
                <div className="user-accommodation-modal__loading">Loading rooms…</div>
              ) : (
                <>
                  {accommodationRoomsError && (
                    <div className="user-accommodation-modal__error">
                      {accommodationRoomsError}
                    </div>
                  )}
                  {accommodationRooms.length === 0 && !accommodationRoomsError ? (
                    <div className="user-accommodation-modal__empty">
                      No rooms available. Create a room first to assign this MSP.
                    </div>
                  ) : (
                    <div className="user-accommodation-modal__grid">
                      {accommodationRooms.map((room) => {
                        const occupied = room.assignments?.length || 0;
                        const total = room.totalSlots || 0;
                        const available = Math.max(total - occupied, 0);
                        const currentRoomId = finalAccommodationRoom?._id 
                          ? (typeof finalAccommodationRoom._id === 'string' ? finalAccommodationRoom._id : finalAccommodationRoom._id.toString())
                          : (user?.room && typeof user.room === 'string' ? user.room : user?.room?._id?.toString());
                        const isCurrentRoom = currentRoomId && room._id === currentRoomId;
                        const status =
                          available === 0
                            ? "full"
                            : occupied === 0
                            ? "empty"
                            : "partial";

                        return (
                          <div
                            key={room._id}
                            className={`user-accommodation-modal__card user-accommodation-modal__card--${status} ${
                              isCurrentRoom ? "user-accommodation-modal__card--current" : ""
                            }`}
                          >
                            <div className="user-accommodation-modal__card-header">
                              <h4>{room.name}</h4>
                              <span>
                                {occupied}/{total || "—"} booked
                              </span>
                            </div>
                            {room.description && (
                              <p className="user-accommodation-modal__card-description">
                                {room.description}
                              </p>
                            )}
                            <div className="user-accommodation-modal__card-meta">
                              <span>
                                {available} bed space{available === 1 ? "" : "s"}
                              </span>
                            </div>
                            <div className="user-accommodation-modal__card-actions">
                              <button
                                type="button"
                                disabled={accommodationActionLoading || isCurrentRoom}
                                onClick={() => handleAssignAccommodation(room._id)}
                              >
                                {isCurrentRoom
                                  ? "Current room"
                                  : accommodationActionLoading
                                  ? "Assigning…"
                                  : `Assign ${isStaff ? "Staff" : "MSP"}`}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        </>
      )}
      
      {/* Checkout Modal */}
      {checkoutModalOpen && (
        <div className="modal-overlay" style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "24px",
            maxWidth: "500px",
            width: "90%",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}>
            <h3 style={{ marginTop: 0, marginBottom: "16px" }}>
              Checkout {isStaff ? "Staff" : "MSP"}
            </h3>
            {canManageAccommodation && (
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", fontSize: "14px" }}>
                  End Date (Optional - for backdating):
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="date"
                    value={checkoutEndDate}
                    onChange={(e) => setCheckoutEndDate(e.target.value)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      fontSize: "14px",
                      flex: 1,
                    }}
                    title="Leave empty to use today's date, or select a date to backdate the checkout"
                  />
                  {checkoutEndDate && (
                    <button
                      type="button"
                      onClick={() => setCheckoutEndDate("")}
                      style={{
                        padding: "6px 12px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        backgroundColor: "#f5f5f5",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            )}
            {(() => {
              const costEstimate = calculateCostEstimate();
              if (costEstimate && canManageAccommodation) {
                const formattedCost = new Intl.NumberFormat('en-NG', {
                  style: 'currency',
                  currency: 'NGN',
                  minimumFractionDigits: 2,
                }).format(costEstimate.totalCost);
                
                const formattedPerNight = new Intl.NumberFormat('en-NG', {
                  style: 'currency',
                  currency: 'NGN',
                  minimumFractionDigits: 2,
                }).format(costEstimate.costPerNight);
                
                return (
                  <div style={{ 
                    marginBottom: "16px", 
                    padding: "12px", 
                    backgroundColor: "#f0f9ff", 
                    borderRadius: "6px",
                    border: "1px solid #bae6fd"
                  }}>
                    <div style={{ fontWeight: "600", marginBottom: "8px", color: "#0369a1" }}>
                      Estimated Accommodation Cost
                    </div>
                    <div style={{ fontSize: "20px", fontWeight: "bold", color: "#0c4a6e", marginBottom: "4px" }}>
                      {formattedCost}
                    </div>
                    <div style={{ fontSize: "13px", color: "#64748b" }}>
                      {costEstimate.nights} night{costEstimate.nights !== 1 ? 's' : ''} × {formattedPerNight} per night
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => {
                  setCheckoutModalOpen(false);
                  setCheckoutEndDate("");
                }}
                style={{
                  padding: "8px 16px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  backgroundColor: "#f5f5f5",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmCheckout}
                disabled={accommodationActionLoading}
                style={{
                  padding: "8px 16px",
                  borderRadius: "4px",
                  border: "none",
                  backgroundColor: "#dc3545",
                  color: "white",
                  cursor: accommodationActionLoading ? "not-allowed" : "pointer",
                  opacity: accommodationActionLoading ? 0.6 : 1,
                }}
              >
                {accommodationActionLoading ? "Processing..." : "Confirm Checkout"}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default User;

